import { ComponentProps, ComponentPropsWithoutRef, ForwardedRef, forwardRef } from 'react';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import { UniqueIdentifier, useDraggable, useDroppable } from '@givebutter/primitives/draggable';
import { faChevronUp, faList, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { useTaskDrawerState } from 'components/tasks/useTaskDrawerState';
import { useResponsive } from 'contexts/useResponsive';
import { twMerge } from 'tailwind-merge';

interface TaskGroupProps extends ComponentProps<'div'> {
    id: string;
}
const TaskGroup = ({ children, className, id, ...restProps }: TaskGroupProps) => {
    const { setNodeRef, isOver } = useDroppable({ id });
    const { collapsedGroups, setCollapsedGroups } = useTaskDrawerState();

    return (
        <div
            className={twMerge(
                'border-t border-gray-200 first:border-t-0',
                isOver && 'bg-gray-100',
                className,
            )}
            ref={setNodeRef}
            {...restProps}
        >
            <Collapsible.Root
                open={!collapsedGroups.includes(id)}
                onOpenChange={isOpen => {
                    setCollapsedGroups(
                        isOpen ? collapsedGroups.filter(g => g !== id) : [...collapsedGroups, id],
                    );
                }}
            >
                {children}
            </Collapsible.Root>
        </div>
    );
};

interface TaskGroupContainerProps extends ComponentPropsWithoutRef<'div'> {}
const TaskGroupContainer = forwardRef(
    (
        { children, className, id, ...restProps }: TaskGroupContainerProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div
                className={twMerge('flex flex-col items-start gap-2 py-6', className)}
                ref={forwardedRef}
                {...restProps}
            >
                {children}
            </div>
        );
    },
);

interface TaskGroupHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const TaskGroupHeader = forwardRef(
    (
        { children, className, ...restProps }: TaskGroupHeaderProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div
                className={twMerge('flex w-full justify-between px-8', className)}
                ref={forwardedRef}
                {...restProps}
            >
                {children}
            </div>
        );
    },
);

interface TaskGroupTitleProps extends ComponentProps<'div'> {}
const TaskGroupTitle = forwardRef(
    (
        { children, className, ...restProps }: TaskGroupTitleProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div
                className={twMerge('text-base font-medium', className)}
                ref={forwardedRef}
                {...restProps}
            >
                {children}
            </div>
        );
    },
);

interface TaskGroupCountProps extends ComponentProps<'div'> {}
const TaskGroupCount = forwardRef(
    (
        { children, className, ...restProps }: TaskGroupCountProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div
                className={twMerge('text-base text-gray-500', className)}
                ref={forwardedRef}
                {...restProps}
            >
                {children}
            </div>
        );
    },
);

interface TaskGroupAddActionProps extends Omit<ButtonProps, 'variant'> {
    variant?: 'icon' | 'text';
}
const TaskGroupAddAction = forwardRef(
    (
        { children, className, variant = 'icon', ...restProps }: TaskGroupAddActionProps,
        forwardedRef: ForwardedRef<HTMLButtonElement>,
    ) => {
        const isIcon = variant === 'icon';
        return (
            <Button
                variant="transparent"
                size="sm"
                shape={isIcon ? 'square' : undefined}
                className={twMerge(
                    'text-gray-500',
                    variant === 'icon' && 'hover:!bg-gray-100',
                    className,
                )}
                ref={forwardedRef}
                {...restProps}
            >
                {isIcon ? (
                    <>
                        <span className="sr-only">Add task</span>
                        <FontAwesomeIcon icon={faPlus} className="text-gray-500" />
                    </>
                ) : null}

                {variant === 'text' ? (
                    <span className="pl-2 text-gray-600">Add task...</span>
                ) : null}
            </Button>
        );
    },
);

const TaskGroupContent = ({ className, ...restProps }: Collapsible.CollapsibleContentProps) => {
    return <Collapsible.Content className={twMerge('w-full', className)} {...restProps} />;
};

const TaskGroupCollapseTrigger = (props: Collapsible.CollapsibleTriggerProps) => {
    return (
        <Collapsible.Trigger {...props} asChild>
            <Button
                variant="none"
                className="data-[state=closed]:rotate-180"
                aria-label="Toggle group"
            >
                <FontAwesomeIcon icon={faChevronUp} className="text-gray-500" />
            </Button>
        </Collapsible.Trigger>
    );
};

const TaskGroupEmpty = () => {
    return (
        <div className="flex w-full flex-col items-center gap-4 px-4 py-8">
            <BoxIcon size="lg">
                <FontAwesomeIcon icon={faList} />
            </BoxIcon>
            <div className="flex flex-col justify-center gap-2 text-center">
                <p className="text-base font-medium">Create your first task</p>
                <p className="max-w-96 text-sm">
                    Keep track of your donor relationships within Givebutter. Assign tasks to team
                    members, link contact information, and create automated tasks.
                </p>
            </div>
        </div>
    );
};

const TaskGroupNotFound = () => {
    return (
        <div className="flex w-full flex-col items-center gap-4 px-4 py-8">
            <BoxIcon size="lg">
                <FontAwesomeIcon icon={faList} />
            </BoxIcon>
            <div className="flex flex-col justify-center gap-2 text-center">
                <p className="text-base font-medium">No tasks found</p>
                <p className="max-w-96 text-sm">Try adjusting your filters</p>
            </div>
        </div>
    );
};

interface TaskGroupDraggableItemProps extends ComponentPropsWithoutRef<'li'> {
    itemId: UniqueIdentifier;
}
const TaskGroupDraggableItem = ({ itemId, className, ...rest }: TaskGroupDraggableItemProps) => {
    const { isMobile } = useResponsive();
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: itemId,
        disabled: isMobile,
    });

    return (
        <li
            ref={setNodeRef}
            style={{
                opacity: isDragging ? 0.5 : 1,
            }}
            data-dragging={isDragging}
            className={twMerge('group', className)}
            {...listeners}
            {...attributes}
            {...rest}
        />
    );
};

const Root = TaskGroup;
const Container = TaskGroupContainer;
const Header = TaskGroupHeader;
const Title = TaskGroupTitle;
const Count = TaskGroupCount;
const AddAction = TaskGroupAddAction;
const Content = TaskGroupContent;
const CollapseTrigger = TaskGroupCollapseTrigger;
const Empty = TaskGroupEmpty;
const NotFound = TaskGroupNotFound;
const DraggableItem = TaskGroupDraggableItem;

export {
    TaskGroup,
    TaskGroupContainer,
    TaskGroupHeader,
    TaskGroupTitle,
    TaskGroupCount,
    TaskGroupAddAction,
    TaskGroupContent,
    TaskGroupCollapseTrigger,
    TaskGroupEmpty,
    TaskGroupNotFound,
    TaskGroupDraggableItem,

    //
    Root,
    Container,
    Header,
    Title,
    Count,
    AddAction,
    Content,
    CollapseTrigger,
    Empty,
    NotFound,
    DraggableItem,
};
